import { createTheme } from '@mui/material/styles'

// Central design tokens. All page/component code should pull colours, radii
// and elevations from here instead of hardcoding hex values.
const tokens = {
  // Primary: warm salmon (Lachs)
  salmon: {
    main: '#F4978E',
    light: '#FAD4D4',
    lighter: '#FDEDEB',
    dark: '#D97B6F',
  },
  // Secondary: soft sage
  sage: {
    main: '#A8C3A1',
    light: '#DDE8D9',
    dark: '#7D9C77',
  },
  success: {
    main: '#7FB685',
    light: '#DDEEDF',
    dark: '#4F8554',
  },
  warning: {
    main: '#E6B17E',
    light: '#F8E3CE',
    dark: '#B07A47',
  },
  error: {
    main: '#D88383',
    light: '#F3D2D2',
    dark: '#A55353',
  },
  info: {
    main: '#88A8C8',
    light: '#DCE7F1',
    lighter: '#EEF3F8',
    dark: '#5C7893',
  },
  neutral: {
    text: '#2F2A28',
    textSecondary: '#6B6360',
    textDisabled: '#B0A8A5',
    divider: '#ECE3E0',
    surface: '#FFFFFF',
    surfaceDefault: '#FFFBFA',
    surfaceSubtle: '#F7F1EF',
  },
}

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.salmon.main,
      light: tokens.salmon.light,
      dark: tokens.salmon.dark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: tokens.sage.main,
      light: tokens.sage.light,
      dark: tokens.sage.dark,
      contrastText: '#FFFFFF',
    },
    success: {
      main: tokens.success.main,
      light: tokens.success.light,
      dark: tokens.success.dark,
      contrastText: '#FFFFFF',
    },
    warning: {
      main: tokens.warning.main,
      light: tokens.warning.light,
      dark: tokens.warning.dark,
      contrastText: '#FFFFFF',
    },
    error: {
      main: tokens.error.main,
      light: tokens.error.light,
      dark: tokens.error.dark,
      contrastText: '#FFFFFF',
    },
    info: {
      main: tokens.info.main,
      light: tokens.info.light,
      lighter: tokens.info.lighter,
      dark: tokens.info.dark,
      contrastText: '#FFFFFF',
    },
    divider: tokens.neutral.divider,
    text: {
      primary: tokens.neutral.text,
      secondary: tokens.neutral.textSecondary,
      disabled: tokens.neutral.textDisabled,
    },
    background: {
      default: tokens.neutral.surfaceDefault,
      paper: tokens.neutral.surface,
      subtle: tokens.neutral.surfaceSubtle,
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.neutral.surfaceDefault,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: false,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 1px 2px rgba(54, 30, 25, 0.08)',
          '&:hover': {
            boxShadow: '0 2px 6px rgba(54, 30, 25, 0.12)',
          },
        },
        outlined: {
          borderColor: tokens.neutral.divider,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: tokens.neutral.divider,
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          borderColor: tokens.neutral.divider,
          boxShadow: '0 1px 2px rgba(54, 30, 25, 0.04)',
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.neutral.surfaceSubtle,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: tokens.neutral.text,
        },
        root: {
          borderBottomColor: tokens.neutral.divider,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '&.Mui-selected': {
            backgroundColor: tokens.salmon.lighter,
            color: tokens.salmon.dark,
            '&:hover': {
              backgroundColor: tokens.salmon.lighter,
            },
            '& .MuiListItemIcon-root': {
              color: tokens.salmon.dark,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.neutral.divider,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: tokens.neutral.divider,
        },
      },
    },
  },
})

export default theme
